import { EmployeeBenefitType } from 'apps/user/types/on-boarding.type';
import { useCreateEmployeeBenefit } from 'common/queries-and-mutations/employee-benefit';
import { Icon, Icons } from 'components/Icon';
import { InfoModal } from 'components/Modal';
import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { Alert } from 'components/Toast';
import Button from 'components/Form/Button';
import CustomSelect from 'components/Form/CustomSelect';
import { employeeBenefits } from 'constants/employee-benefits';
import TextField from 'components/Form/TextField';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

export default function AddBenefitModal({ onClose, onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isLoading, isError } = useCreateEmployeeBenefit();

  const formik = useFormik({
    initialValues: {
      employerBenefit: [
        {
          employeeBenefitId: null,
          employerName: '',
          employerAddress: '',
          employeeId: '',
          employeeBenefit: ''
        }
      ]
    },
    validationSchema: Yup.object({
      employerBenefit: Yup.array().of(
        Yup.object().shape({
          employerName: Yup.string()
            .required('Employer name is required')
            .nullable(),
          employerAddress: Yup.string().required(
            'Employer address is required'
          ),
          employeeId: Yup.string()
            .required('Employer ID is required')
            .nullable(),
          employeeBenefit: Yup.string().required(
            'Please select type of benefits'
          )
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onBenefitSubmit(values);
    }
  });

  const onBenefitSubmit = (formValue: EmployeeBenefitType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      toast(<Alert message="Benefit Successfully Added" type="success" />);
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  return (
    <div>
      <InfoModal width="w-[60%]" className="h-fit rounded-md">
        <div className="flex flex-row items-center justify-between border-b p-3 pl-6">
          <p className="text-2xl font-bold text-[#353535]">
            Add Employee Benefit
          </p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="p-8"
          onSubmit={formik.handleSubmit}
        >
          {formik.values.employerBenefit.map((benefit, index) => (
            <div key={index} className="mb-1 mt-8">
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`employerBenefit[${index}].employerName`}
                    placeholder=""
                    value={benefit.employerName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Name of Employer"
                    error={
                      formik.touched.employerBenefit
                        ? formik.errors.employerBenefit?.[index] &&
                          formik.errors.employerBenefit.length > 0
                          ? JSON.parse(
                              JSON.stringify(
                                formik.errors.employerBenefit[index]
                              )
                            ).employerName
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`employerBenefit[${index}].employerAddress`}
                    placeholder=""
                    value={benefit.employerAddress}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Address of Employer"
                    error={
                      formik.touched.employerBenefit
                        ? formik.errors.employerBenefit?.[index] &&
                          formik.errors.employerBenefit.length > 0
                          ? JSON.parse(
                              JSON.stringify(
                                formik.errors.employerBenefit[index]
                              )
                            ).employerAddress
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`employerBenefit[${index}].employeeId`}
                    placeholder=""
                    value={benefit.employeeId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Employer ID"
                    error={
                      formik.touched.employerBenefit
                        ? formik.errors.employerBenefit?.[index] &&
                          formik.errors.employerBenefit.length > 0
                          ? JSON.parse(
                              JSON.stringify(
                                formik.errors.employerBenefit[index]
                              )
                            ).employeeId
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                    isRequired
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <CustomSelect
                    options={employeeBenefits}
                    selectedOption={{
                      value: benefit.employeeBenefit,
                      label: benefit.employeeBenefit
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(
                        `employerBenefit[${index}].employeeBenefit`,
                        option?.value
                      );
                    }}
                    label="Select Benefit"
                    name="employeeBenefit"
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.employerBenefit
                        ? formik.errors.employerBenefit?.[index] &&
                          formik.errors.employerBenefit.length > 0
                          ? JSON.parse(
                              JSON.stringify(
                                formik.errors.employerBenefit[index]
                              )
                            ).employeeBenefit
                          : ''
                        : ''
                    }
                    isRequired
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="mt-5 flex items-end justify-end gap-8">
            <Button
              variant="primary"
              size="md"
              className="mt-5 w-[9rem] rounded p-[0.1rem]"
              label="Add New Benefit"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </InfoModal>
    </div>
  );
}
